import { Component, OnInit, OnDestroy, ViewChild, ElementRef } from '@angular/core';
import { Router, ActivatedRoute, Data } from "@angular/router";
import { Observable, Subscription, of } from "rxjs";
import { tap, finalize, switchMap, map } from "rxjs/operators";
import { SubHeaderService } from "../../../../core/services/sub-header.service";
import { SubHeader } from "../../../../core/models/sub-header.model";
import { LandownerPassthroughProgramApplication, LandownerPassthroughProgramDecision, LandownerPassthroughProgramNote, LandownerProgramApplicationResponse, LandownerProgramStatus } from "../../../../common/models/permit.model";
import { CanDeactivateComponent } from "../../../../common/classes/can-deactivate.component";
import { DynamicDialogService } from "../../../../core/services/dynamic-dialog.service";
import { Dialog, DialogButton } from "../../../../core/models/dialog.model";
import { AuthService } from "../../../../services/auth.service";
import { LoaderService } from "../../../../common/services/loader.service";
import { PermitService } from "../../../../common/services/permit.service";
import { DocumentManagementService } from "../../../../common/services/document-management.service";
import { parseFileNameFromHeaders } from "../../../../common/models/onBaseIntegrationAndMapping.model";
import { SidePanelService } from '../../../../common/services/side-panel.service';
import { SidePanel } from '../../../../common/models/side-panel.model';
import { SubmitPassthroughPermitComponent } from "../submit-passthrough-permit/submit-passthrough-permit.component";
import { EditLandownerApplicationComponent } from "../edit-landowner-application/edit-landowner-application.component";


@Component({
    selector: 'app-landowner-wrapper',
    templateUrl: './landowner-wrapper.component.html',
    styleUrls: ['./landowner-wrapper.component.scss'],
    standalone: false
})
export class LandownerWrapperComponent extends CanDeactivateComponent implements OnInit, OnDestroy {

    @ViewChild("downloadLink") public downloadLink: ElementRef;
    @ViewChild(SubmitPassthroughPermitComponent) public submissionForm: SubmitPassthroughPermitComponent;
    @ViewChild(EditLandownerApplicationComponent) public editForm: EditLandownerApplicationComponent;


    applicationSuccessfulDialog: Dialog = new Dialog("", [new DialogButton("No", true, () => { this.home(); }), new DialogButton("Yes", true, () => { this.confirmStartNew(); })]);

    editStatuses: LandownerProgramStatus[] = [LandownerProgramStatus.InProcess, LandownerProgramStatus.ReturnedForCorrection];
    reviewStatuses: LandownerProgramStatus[] = [LandownerProgramStatus.AwaitingReview];

    saveSuccessfulDialog: Dialog = new Dialog("", [new DialogButton("OK", true, () => { this.home(); }), new DialogButton("Continue Working", true, () => { return true; })])
    savedText: string = "Your application [XXX] has been saved for future completion.";

    successText: string = "Applicaton has been successfully submitted for approval.  For future reference, the application number is [XXX].  Do you wish to create a new application?";
    approveSuccessText: string = "Applicaton [XXX] has been approved and a permit generated.  Your download should begin shortly.  Do you wish to start a new application?";

    returnedDialog: Dialog = new Dialog("Application [XXX] has been returned for corrections.", [new DialogButton("OK", true, () => { this.home(); })]);

    confirmStartNewDialog: Dialog = new Dialog("Starting a new application will cause any data updated since the last \"Save for Later\" use to be lost beyond retrieval.  Do you wish to continue?", [new DialogButton("NO", false, () => { return false; }), new DialogButton("YES", true, () => { this.confirmStartNew(); })]);

    sidePanel: SidePanel = new SidePanel({
        tabs: [{ title: "Timeline" }, { title: "Comments" }],
        sections: [{ componentName: 'ProgramTimeline', componentData: [] }, { componentName: "ProgramNotes", componentData: {} }]
    });


    private subheader: SubHeader = new SubHeader({ title: "", buttons: [] });
    private subheaderTitle: string = "Landowner Pass-through Payment Program";
    private subheaderText: string = " - [XXX]";

    private _application: LandownerPassthroughProgramApplication;

    get application(): LandownerPassthroughProgramApplication {
        return this._application;
    }

    set application(value: LandownerPassthroughProgramApplication) {        
        this._application = value;
        this.subheader.buttons = [];
        if (this.inEditMode) {
            this.subheader.title = `${this.subheaderTitle} ${this.subheaderText.replace('[XXX]', value.applicationId)}`
            this.subheader.buttons.push({ name: "Start New Application", method: () => this.startNew() });
            this.subheader.buttons.push({ name: "View Timeline & Comments", method: () =>  this.showTimeline() });
        }
        else if (this.inReviewMode) {
            this.subheader.title = `${this.subheaderTitle} ${this.subheaderText.replace('[XXX]', value.applicationId)} <span class='red'>- Review</span>`
            this.subheader.buttons.push({ name: "Return", method: () => this.returnApplication() });
            this.subheader.buttons.push({ name: "Approve", method: () => this.approveApplication() });
            this.subheader.buttons.push({ name: "View Timeline & Comments", method: () => this.showTimeline() });
        }
        else {
            this.subheader.title = this.subheaderTitle;
        }
        this.subheaderService.updateSubHeader(this.subheader);
        
    }

    get inEditMode(): boolean {
        return this.application != null && this.editStatuses.find(s => this.application.programStatus === s) != null;
    }

    get inInputMode(): boolean {
        return this.application == null;
    }

    get inReviewMode(): boolean {
        return this.application != null && this.reviewStatuses.find(s => this.application.programStatus === s) != null;
    }


    subscription: Subscription = new Subscription();

    constructor(dialog: DynamicDialogService, auth: AuthService, private subheaderService: SubHeaderService, private route: ActivatedRoute, private loader: LoaderService, private permitService: PermitService,
        private router: Router, private documentService: DocumentManagementService, private sidePanelService: SidePanelService) {
        super(dialog, auth);
    }

    ngOnInit() {       
        const data$: Observable<Data> = this.route.data.pipe();

        let dataSubs$ = data$.subscribe(
            (data: Data) => {
                this.application = <LandownerPassthroughProgramApplication>data.application;               
            },
            (error) => {
                console.error(error);
            }
        );

        this.subscription.add(dataSubs$);

        let save$ = this.sidePanelService.saveInProgress$.pipe(
            map((data: any) => {
                if (data.dataType === "Landowner Program Comment Added") {
                    return data.payload;
                } else {
                    return null;
                }
            })
        ).subscribe(
            (data: LandownerPassthroughProgramNote | null) => {
                if (data != null) this.application.landownerPassthroughProgramNotes.push(data);
            },
            (error) => { console.error(error); });
        this.subscription.add(save$);
    }

    ngOnDestroy() {
        if (this.subscription != null) this.subscription.unsubscribe();
    }

    approveApplication(): void {
        let decision: LandownerPassthroughProgramDecision = {
            application: this.application,
            statusChange: LandownerProgramStatus.Active
        }
        let obs$ = this.saveDecision(decision).pipe(
            tap((response: LandownerProgramApplicationResponse) => {
                this.applicationSuccessfulDialog.message = this.approveSuccessText.replace("[XXX]", response.applicationId);
            }),
            switchMap((response: LandownerProgramApplicationResponse) => {
                if (response != null && response.queuedDocumentId != null) {
                    return this.documentService.downloadFromQueue(response.queuedDocumentId);
                } else {
                    return of(null);
                }
            }),
            tap((data) => {
                if (data != null) {
                    const fileName = parseFileNameFromHeaders(data.headers.get("content-disposition"));
                    const blob = new Blob([data.body], { type: data.body.type });
					const blobUri = window.URL.createObjectURL(blob);
					const anchor = <HTMLAnchorElement>this.downloadLink.nativeElement;
					anchor.href = blobUri;
					anchor.download = fileName;
					anchor.click();
                }
            }),
            finalize(() => {
                this.loader.toggleLoader(false);
            })
        );
        this.loader.toggleLoader(true);
        let subs$ = obs$.subscribe(
            () => {
                this.allowDeactivate.next(true);
                this.applicationSuccessfulDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.applicationSuccessfulDialog);
            },
            (error) => {
                console.error(error);
                this.dialogService.showError();
            }
        );
        this.subscription.add(subs$);
    }

    confirmStartNew(): void {
        this.application = null;
        if (this.submissionForm != null) this.submissionForm.cancel();
        if (this.editForm != null) this.editForm.cancel();
    }

    home(): void {
        this.allowDeactivate.next(true);
        this.router.navigate(['/home']);
    }

    returnApplication(): void {
        let decision: LandownerPassthroughProgramDecision = {
            application: this.application,
            statusChange: LandownerProgramStatus.ReturnedForCorrection
        }
        let obs$ = this.saveDecision(decision);
        this.loader.toggleLoader(true);
        let subs$ = obs$.pipe(
            finalize(() => { this.loader.toggleLoader(false); })
        ).subscribe(
            (data: any) => {
                this.returnedDialog.message = this.returnedDialog.message.replace("[XXX]", decision.application.applicationId);
                this.returnedDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.returnedDialog);
            },
            (error) => {
                console.error(error);
                this.dialogService.showError();
            });
        this.subscription.add(subs$);
    }

    saveDecision(decision: LandownerPassthroughProgramDecision): Observable<LandownerProgramApplicationResponse> {
        return this.permitService.decisionLandownerPassthroughApplication(decision);
    }

    saveForLater(application: LandownerPassthroughProgramApplication): void {
        if (application != null) {
            this.loader.toggleLoader(true);
            let saveSubs$ = this.permitService.saveLandownerApplication(application).pipe(
                tap((response: LandownerProgramApplicationResponse) => {
                    this.saveSuccessfulDialog.message = this.savedText.replace("[XXX]", response.applicationId);
                    this.saveSuccessfulDialog.toggleDialog(true);
                    this.dialogService.updateDialog(this.saveSuccessfulDialog);
                }),
                finalize(() => { this.loader.toggleLoader(false); })
            ).subscribe((data: LandownerProgramApplicationResponse) => {
                this.allowDeactivate.next(true);
                this.application = data.application;
            },
                (error) => {
                    console.error(error);
                    this.dialogService.showError();
                }
            );
            this.subscription.add(saveSubs$);
        }
    }

    showTimeline(): void {
        this.sidePanel.sections[0].componentData = this.application.landownerPassthroughProgramHistories;
        this.sidePanel.sections[1].componentData = { applicationId: this.application.applicationId, notes: this.application.landownerPassthroughProgramNotes };
        this.sidePanelService.newSidePanel(this.sidePanel);
        this.sidePanelService.updateSectionData("ProgramTimeline", this.application.landownerPassthroughProgramHistories)
        this.sidePanelService.updateSectionData("ProgramNotes", { applicationId: this.application.applicationId, notes: this.application.landownerPassthroughProgramNotes });
        this.sidePanelService.toggleSidePanel(true);
    }

    startNew(): void {
        this.confirmStartNewDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.confirmStartNewDialog);
    }

    submitApplication(application: LandownerPassthroughProgramApplication): void {
        if (application.programStatus != LandownerProgramStatus.AwaitingReview) application.programStatus = LandownerProgramStatus.AwaitingReview;
        this.loader.toggleLoader(true);
        let saveSubs$ = this.permitService.submitLandownerPassthroughApplication(application).pipe(
            tap((response: LandownerProgramApplicationResponse) => {
                this.applicationSuccessfulDialog.message = this.successText.replace("[XXX]", response.applicationId);
                this.applicationSuccessfulDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.applicationSuccessfulDialog);
            }),
            finalize(() => { this.loader.toggleLoader(false); })
        ).subscribe(
            () => {  },
            (error) => {
                console.error(error);
                this.dialogService.showError();
            }
        );
        this.subscription.add(saveSubs$);
    }
}
